import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import * as THREE from 'three';

interface ParticleTransitionProps {
  onTransitionComplete?: () => void;
  isActive: boolean;
}

/**
 * ParticleTransition Component
 *
 * Smooth, professional transition from particles to cards
 * Uses a subtle backdrop fade instead of text for a clean effect
 */
export const ParticleTransition: React.FC<ParticleTransitionProps> = ({
  onTransitionComplete,
  isActive,
}) => {
  const hasTransitioned = useRef(false);

  useEffect(() => {
    if (isActive && !hasTransitioned.current) {
      hasTransitioned.current = true;

      // Transition complete callback after animation
      const timer = setTimeout(() => {
        if (onTransitionComplete) {
          onTransitionComplete();
        }
      }, 2500);

      return () => clearTimeout(timer);
    }
  }, [isActive, onTransitionComplete]);

  if (!isActive) {
    hasTransitioned.current = false;
    return null;
  }

  return (
    <>
      {/* Subtle flash effect at start */}
      <motion.div
        className="absolute inset-0 pointer-events-none bg-white"
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 0.15, 0] }}
        transition={{
          duration: 0.4,
          times: [0, 0.3, 1],
          ease: "easeOut"
        }}
        style={{ zIndex: 99 }}
      />

      {/* Smooth backdrop transition - no text */}
      <motion.div
        className="absolute inset-0 pointer-events-none bg-navy-blue"
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 0.3, 0] }}
        transition={{
          duration: 2.5,
          times: [0, 0.35, 1],
          ease: "easeInOut"
        }}
        style={{ zIndex: 100 }}
      />
    </>
  );
};

/**
 * Calculate 2D grid positions for cards based on screen dimensions
 */
export const calculateCardGridPositions = (
  cardCount: number,
  containerWidth: number,
  containerHeight: number,
  columnsCount: number = 4
): Array<{ x: number; y: number }> => {
  const positions: Array<{ x: number; y: number }> = [];
  const cardWidth = containerWidth / columnsCount;
  const cardHeight = 300; // Approximate card height
  const padding = 16;

  // Calculate grid layout
  // const rows = Math.ceil(cardCount / columnsCount);

  for (let i = 0; i < cardCount; i++) {
    const col = i % columnsCount;
    const row = Math.floor(i / columnsCount);

    const x = col * cardWidth + (cardWidth / 2);
    const y = row * cardHeight + (cardHeight / 2) + padding;

    positions.push({ x, y });
  }

  return positions;
};

/**
 * Map 3D particle positions to 2D screen coordinates
 */
export const map3DTo2D = (
  position3D: THREE.Vector3,
  camera: THREE.Camera,
  width: number,
  height: number
): { x: number; y: number } => {
  const vector = position3D.clone();
  vector.project(camera);

  const x = (vector.x + 1) / 2 * width;
  const y = -(vector.y - 1) / 2 * height;

  return { x, y };
};

/**
 * Generate transition path from particle to card with easing
 */
export const generateTransitionPath = (
  startPos: { x: number; y: number },
  endPos: { x: number; y: number },
  steps: number = 60
): Array<{ x: number; y: number }> => {
  const path: Array<{ x: number; y: number }> = [];

  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    // Cubic bezier easing
    const ease = t < 0.5
      ? 4 * t * t * t
      : 1 - Math.pow(-2 * t + 2, 3) / 2;

    const x = startPos.x + (endPos.x - startPos.x) * ease;
    const y = startPos.y + (endPos.y - startPos.y) * ease;

    path.push({ x, y });
  }

  return path;
};

export default ParticleTransition;
